/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Dayjs } from "dayjs";
import React from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { DeliveryDetails } from "../../../cart/storage/cart.types";
import { SalesAgreement, selectSalesAgreementById } from "../../../sales-agreements/storage";
import { StoreInfo } from "../../../stores/storage/stores.types";

import { DrawdownConfig, ProductRequest, RequestMonth } from "./types";

export interface IDrawdownRequestState {
  tabIndex: number;
  contactDetails:
    | {
        name: string;
        email: string;
        phone: string;
        reference?: string;
      }
    | undefined;
  productRequest: Array<ProductRequest>;
  selectedMonth: RequestMonth | null;
  drawdownConfig: DrawdownConfig | null;
  userEdited?: {
    addressSelected?: DeliveryDetails;
    pickupStoreSelected?: StoreInfo;
    deliveryInstructions?: string;
    dairySupplyNumber?: string;
    preferredDate?: Dayjs;
  };
  notes: string;
}

type IDrawdownRequestContext = {
  state: IDrawdownRequestState;
  setState: React.Dispatch<Partial<IDrawdownRequestState>>;
  salesAgreement: SalesAgreement | undefined;
  reset: () => void;
};

const DrawdownRequestContext = React.createContext<IDrawdownRequestContext | null>(null);

const initState: IDrawdownRequestState = {
  tabIndex: 0,
  contactDetails: undefined,
  productRequest: [],
  selectedMonth: null,
  drawdownConfig: null,
  notes: "",
};

interface DrawdownRequestContextProviderProps {
  children: React.ReactNode;
}

const DrawdownRequestContextProvider: React.FC<DrawdownRequestContextProviderProps> = ({
  children,
}) => {
  const [state, setState] = React.useReducer(
    (prev: IDrawdownRequestState, next: Partial<IDrawdownRequestState>) => ({ ...prev, ...next }),
    initState
  );

  const reset = () => {
    setState(initState);
  };

  const params = useParams();
  const salesAgreementId = params.salesAgreementId;
  const salesAgreement = useSelector(selectSalesAgreementById(salesAgreementId));

  const memoizedValue = React.useMemo(
    () => ({ state, setState, salesAgreement, reset }),
    [state, salesAgreement]
  );

  return (
    <DrawdownRequestContext.Provider value={memoizedValue}>
      {children}
    </DrawdownRequestContext.Provider>
  );
};

function withDrawdownRequestContextProvider<T extends object>(Component: React.ComponentType<T>) {
  return (props: T) => {
    return (
      <DrawdownRequestContextProvider>
        <Component {...props} />
      </DrawdownRequestContextProvider>
    );
  };
}

function useDrawdownRequestContext() {
  const contextValue = React.useContext(DrawdownRequestContext);
  if (contextValue === null) {
    throw Error("DrawdownRequestContext has not been Provided!");
  }
  return contextValue;
}

export { useDrawdownRequestContext, withDrawdownRequestContextProvider };
